import { List } from 'immutable';
import { ManagementMetadata } from './schema';
import { IResourceGrant } from './resourceGrant';

// Although entity_group is an Entity in Delphius, the serializers
// used in the API make the id come back as a number, which is the
// only field currently used in our code
interface IEntityGroup {
  id: number;
}
interface IControlPanelMenuItem {
  id: string;
  name: string;
  location_template: string;
  icon: string;
  requires_entity: boolean;
  version: any;
  requires_feature_permission: string;
}

interface IAbility {
  create?: boolean;
  read?: boolean;
  destroy?: boolean;
  update?: boolean;
  read_all?: boolean;
  update_name?: boolean;
  //tslint:disable-next-line:no-reserved-keywords
  delete?: boolean;
  update_is_private?: boolean;
}

interface IEntityBase {
  id: string;
  name: string;
  state: string;
  country: string;
  subdomain: string;
  display_name: string;
}

export interface IEntity extends IEntityBase {
  address?: string;
  entity_type:
    | {
        name: string;
        value: string;
      }
    | string;
  homepage_url: string;
  logo: string;
  entity_id: number;
  default_coa_id: string;
  color: {
    color: string;
    index: number;
  };
  fiscal_year_start_month: number;
  population: number;
  fiscal_year_start_date_formatted: string;
  default_report_id: string;
  budget_101: boolean;
  feature_permissions: string[];
  report_preferences: {
    expenses_variance_formula: string;
    revenues_variance_formula: string;
  };
  active: boolean;
  published: boolean;
  emblem: any;
  transparency_url: string;
  tos_url: string;
  accepted_budget_license_agreement: boolean;
  created_at: string;
  longitude: string | number;
  latitude: string | number;
  abilities: IAbility & { unpublish: boolean; upload_logo: boolean; manage: boolean; switch_to: boolean };
  salesforce_id: string;
  uuid?: string;
  entity_group?: IEntityGroup;
}

// Response from v1/session endpoint
interface ISession {
  id: number;
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: {
    name: string;
    display_name: string;
  };
  abilities: {
    entity: IAbility & { switch_to: boolean };
    report: IAbility;
    saved_view: IAbility;
    'data_manager/coa_mask': IAbility;
    feedback: IAbility & { forward: boolean };
    'budgeting/budget': IAbility;
    'budgeting/budget_invite': IAbility;
    feature_license_agreement: IAbility;
    user: IAbility & { promote: boolean };
    api_key: IAbility & { manage: boolean };
  };
  demote_at: string;
  environment: string;
  avatar: string;
  control_panel_menu_items: IControlPanelMenuItem[];
  user_intercom_hash: string;
  groups: string[];
  title: string;
  department: string;
  last_seen: string;
  created_at: string;
  entity: IEntity;
}

type IReportCreator = ISession & {
  not_found: boolean;
  is_admin?: boolean;
};

export enum EReportVisibility {
  public = 'public',
  private = 'private'
}

export enum EReportGridValueFormat {
  NUMERIC = 'numeric',
  TEXT = 'text'
}

export enum EReportCreatedByFitler {
  ANYONE = 'ANYONE',
  ME = 'ME',
  NOT_ME = 'NOT_ME'
}

export enum EReportVisibilityFilter {
  ALL = 'ALL',
  INTERNAL = 'INTERNAL',
  PUBLIC = 'PUBLIC'
}

export enum EReportSortOption {
  LAST_UPDATED_NEWEST = 'LAST_UPDATED_NEWEST',
  LAST_UPDATED_OLDEST = 'LAST_UPDATED_OLDEST',
  NAME_A_Z = 'NAME_A_Z',
  NAME_Z_A = 'NAME_Z_A',
  DATA_UPDATED_NEWEST = 'DATA_UPDATED_NEWEST',
  DATA_UPDATED_OLDEST = 'DATA_UPDATED_OLDEST'
}

export enum EReportViewOption {
  LIST = 'LIST',
  GRID = 'GRID'
}

export enum EReportsListV4ItemType {
  ACCOUNT_SUMMARY_REPORT = 'account-summary',
  CHART_AND_DETAILS_REPORT = 'chart-and-details',
  FOLDER = 'folder'
}

export enum ReportGalleryItemType {
  STANDARD = 'standard',
  CUSTOM = 'custom'
}

export const FTE_COUNTS = 'FTE Counts';

export interface IReport {
  id?: string;
  lensId?: string; // Dataset service lens id
  viewId?: string; // Delphius view identifier
  entityId: number;
  isDimensional: boolean;
  isMultiDataset: boolean;
  title: string;
  description: string;
  defaultReportConfiguration: string;
  visibility: EReportVisibility;
  allowedActions?: Map<string, boolean>;
  datasetIds?: string[];
  reportConfigurationId?: string;
  savedViewId?: string;
  parentFolderId?: number | null;
  isReadOnly?: boolean;
  forceCoA?: string;
  defaultSavedViewId?: string | null;
  /**
   * Non-null marks this a combined report covering an entity group. Only a multi-hierarchy
   * report may carry one — the report visibility rule reads a null here as "not a combined
   * report" rather than joining the configuration to test its variant.
   */
  entityGroupId?: string | null;
}

export interface IReportsListV4Item {
  type: EReportsListV4ItemType;
  title: string;
  createdByUser: IReportCreator;
  parentFolderId: number | null;
  createdAt: string;
  updatedAt: string;
  //Owners will be granted in the report settings, the users will be the same type as creator
  owners?: IReportCreator[];
}

export interface IFolder extends IReportsListV4Item {
  type: EReportsListV4ItemType.FOLDER;
  id: number;
  entityId: number;
  subfolders?: IFolder[];
}

export interface IReportV4Dataset {
  id: string;
  name: string;
  standardType: string | null;
  managed: ManagementMetadata;
  deletedAt?: string | null;
}

export interface IReportV4 extends IReportsListV4Item {
  type: EReportsListV4ItemType.ACCOUNT_SUMMARY_REPORT | EReportsListV4ItemType.CHART_AND_DETAILS_REPORT;
  id: string;
  datasets: IReportV4Dataset[];
  description: string;
  isDimensional: boolean;
  isMultiDataset: boolean;
  reportConfigurationId: string;
  savedViewId: string;
  visibility: 'public' | 'private';
  isReadOnly?: boolean;
}

// Common data structure for listing Rv1/Rv2 Reports together
//
export interface IMergedReport {
  id: string;
  legacyReportId?: string;
  legacyReportType?: string;
  name: string;
  description?: string;
  isPrivate: boolean;
  version: string;
  url?: string;
  isGeoEnabled?: boolean;
}

export interface IReportMetadata {
  id: string;
  createdBy: string;
  createdByUser?: IReportCreator;
  updatedBy: string;
  deletedAt: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  visibility: string;
  parentFolderId?: number | null;
  owners?: IResourceGrant[];
}

export interface IReportGalleryItem {
  datasetList?: List<IReportGalleryItemDatasetListItem>;
  id: string;
  title: string;
  createdBy?: string;
  createdByUser?: IReportCreator;
  description: string;
  dataUpdated: string;
  dataset: string;
  datasetPath?: string;
  datasetPresent?: boolean;
  href: string;
  isAccountSummaryReport: boolean;
  isMultiDataset?: boolean;
  isPrivate: boolean;
  showDatasetLink?: boolean;
  lastUpdated?: string;
  navigateToReport(navigate?: ReportNavigateFunction): void;
}

/**
 * Structural stand-in for react-router's `NavigateFunction`, so consumers of this package do not need
 * react-router-dom installed to type-check it. A real `NavigateFunction` is assignable to it.
 */
export type ReportNavigateFunction = (to: string, options?: { replace?: boolean; state?: unknown }) => void;

export interface IReportGalleryItemDatasetListItem {
  name: string;
  path: string;
}

export interface IReportGalleryFilters {
  search: string;
  createdBy: EReportCreatedByFitler;
  visibility: EReportVisibilityFilter;
  sort: EReportSortOption;
  view: EReportViewOption;
  isLoaded: boolean;
}

export type ReportSelectFilters = {
  key: EReportCreatedByFitler | EReportVisibilityFilter | EReportSortOption;
  label: string;
  default: boolean;
};

export type IPaginatedNavigation = {
  offset: number;
  limit: number;
};

export type IPaginatedMeta = {
  total: number;
  prev: IPaginatedNavigation;
  next: IPaginatedNavigation;
};

export interface IPaginatedResponse<T> {
  data: T[];
  meta: IPaginatedMeta;
}

export type IPaginatedReportsResponse = {
  reports: IReportResponse[];
  meta: IPaginatedMeta;
};

export type IReportGalleryPagination = {
  current: IPaginatedNavigation;
  meta: IPaginatedMeta;
};

export type IReportPayload = IReport;
export type IReportResponse = IReport & IReportMetadata;

export interface ILegacyReport {
  id: number;
  entity_id: number;
  entity: string;
  coa_id: string;
  report_type: string;
  report_type_name: string;
  access_code?: string;
  hidden: boolean;
  name: string;
  description: string;
  properties: any;
  coa_mask_id: null;
  created: string;
  updated: string;
  is_default: boolean;
  is_private: boolean;
  position: number;
  owner: any;
  visibility: string;
  is_geo_enabled?: boolean;
}

// Common data structure for listing Rv1/Rv2 Reports together
export interface ICurrentOrLegacyReport {
  id: string;
  legacyReportId?: string;
  legacyReportType?: string;
  name: string;
  description?: string;
  isPrivate: boolean;
  version: string;
  isGeoEnabled?: boolean;
  url?: string;
  isMultiDataset?: boolean;
}
